import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(formData.email, formData.password);
      toast.success('Zalogowano pomyślnie!');
      navigate(userData.user_type === 'company' ? '/dashboard/company' : '/dashboard/client');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="login-page">
      <SEO 
        title="Logowanie"
        description="Zaloguj się do Giełda Budowlana. Zarządzaj swoimi ogłoszeniami i kontaktuj się z wykonawcami."
        canonical="/login"
        noindex={true}
      />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">Giełda Budowlana</span>
          </Link>
          <CardTitle className="font-heading text-2xl">Zaloguj się</CardTitle>
          <CardDescription>Wprowadź swoje dane logowania</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="twoj@email.pl"
                required
                data-testid="input-email"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Hasło</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Twoje hasło"
                required
                data-testid="input-password"
              />
              <div className="mt-1 text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-orange-600 hover:text-orange-700"
                  data-testid="forgot-password-link"
                >
                  Zapomniałeś hasła?
                </Link>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </Button>
          </form>

          {/* Google Login - tymczasowo wyłączone */}

          <p className="text-center text-sm text-slate-600 mt-6">
            Nie masz konta?{' '}
            <Link to="/register" className="text-orange-600 hover:text-orange-700 font-medium">
              Zarejestruj się
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
